











































































































































































import { Component } from "vue-property-decorator";
import MiraBarRowComponent from "@/components/mira/MiraBarRow.vue";
import MiraDetailCardComponent from "@/components/mira/MiraDetailCard.vue";
import MiraDonutWithLegendComponent from "@/components/mira/MiraDonutWithLegend.vue";
import MiraFactsComponent from "@/components/mira/MiraFacts.vue";
import MiraGroupedBarRowsComponent from "@/components/mira/MiraGroupedBarRows.vue";
import MiraKpiTileComponent from "@/components/mira/MiraKpiTile.vue";
import MiraKpiTileGridComponent from "@/components/mira/MiraKpiTileGrid.vue";
import MiraComparisonCardComponent from "@/components/mira/MiraComparisonCard.vue";
import MiraScreenBase from "@/components/mira/MiraScreenBase";
import MiraScreenHeaderComponent from "@/components/mira/MiraScreenHeader.vue";
import MiraTrendCardComponent from "@/components/mira/MiraTrendCard.vue";
import MiraStackedTrendCardComponent from "@/components/mira/MiraStackedTrendCard.vue";
import MiraUnlockListComponent from "@/components/mira/MiraUnlockList.vue";
import { MiraSection } from "@/data/models/Mira";
import { formatProjectTypeName } from "@/data/models/ProjectDetails";
import {
  getBranchesOfClient,
  getBranchesOfMemberClient,
  getClientsByIds,
  getMemberClientIds,
} from "@/data/repository/ClientRepository";
import { ProjectManagementSettingsModule } from "@/store/modules/ProjectManagementSettingsModule";
import {
  COLOR_MIRA_BAR_TRACK,
  COLOR_MIRA_BAR_TRACK_DARK,
  COLOR_MIRA_MARKET_BAR,
  COLOR_MIRA_MARKET_BAR_DARK,
} from "@/utils/colors";
import {
  buildBranchRows,
  buildBusinessTiles,
  buildOutcomeSegments,
  buildDamageCauseMarket,
  buildDamageCauseRows,
  buildProjectTypeRows,
  damageCauseCoverage,
  damageCauseMarketCoverage,
  MiraDamageCauseMarket,
  businessStackAvailable,
  businessStackCaption,
  businessStackFormatter,
  businessStackLegendValues,
  businessStackSegments,
  businessTrendFormatter,
  outcomeTotalText,
  rejectionFacts,
} from "@/utils/MiraBusinessDevelopmentView";
import { MIRA_MARKET_OWN_ID, MIRA_MARKET_PROJECTS_ID } from "@/utils/MiraComparisonView";
import { companyViewOf } from "@/utils/MiraCompanyView";
import { MiraStackedSegmentDef, buildStableEntityColors } from "@/utils/MiraStackedTrendView";
import { MiraSectionUnlocks, collectUnlocks } from "@/utils/MiraDataQuality";
import { MiraBar, MiraSegment, MiraTile } from "@/utils/MiraDataQualityView";
import { MiraFact } from "@/utils/MiraDryingView";
import { themed } from "@/utils/Theme";

/**
 * Mira Geschäftsentwicklung: projects started, project duration, rooms per project, reaction time and
 * rejections, with the outcome mix and the projects per branch and per project type.
 *
 * Built like Trocknung and Geräte (`MiraScreenBase` for loading, the period picker and the header).
 * Ablehnungsquote gets a tile only above 0, and Reaktionszeit counts only damage days really entered
 * (Taimoor, 2026-10-01; the second is applied by IRES Cloud before publishing). Branch and project type
 * names are resolved here, so the view model never imports the repositories. See
 * `docs/analytics/mira-office-screens.md`.
 */
@Component({
  components: {
    "mira-bar-row": MiraBarRowComponent,
    "mira-detail-card": MiraDetailCardComponent,
    "mira-donut-with-legend": MiraDonutWithLegendComponent,
    "mira-facts": MiraFactsComponent,
    "mira-grouped-bar-rows": MiraGroupedBarRowsComponent,
    "mira-kpi-tile": MiraKpiTileComponent,
    "mira-kpi-tile-grid": MiraKpiTileGridComponent,
    "mira-screen-header": MiraScreenHeaderComponent,
    "mira-comparison-card": MiraComparisonCardComponent,
    "mira-trend-card": MiraTrendCardComponent,
    "mira-stacked-trend-card": MiraStackedTrendCardComponent,
    "mira-unlock-list": MiraUnlockListComponent,
  },
})
export default class MiraBusinessDevelopmentComponent extends MiraScreenBase {
  get section(): MiraSection {
    return MiraSection.BUSINESS_DEVELOPMENT;
  }

  get titleKey(): string {
    return "mira.businessDevelopment.title";
  }

  get trackColor(): string {
    return themed(COLOR_MIRA_BAR_TRACK, COLOR_MIRA_BAR_TRACK_DARK);
  }

  get tiles(): MiraTile[] {
    return buildBusinessTiles(this.period, this.comparison, this.viewContext);
  }

  get outcomeSegments(): MiraSegment[] {
    return buildOutcomeSegments(this.period, this.viewContext);
  }

  get outcomeTotalText(): string {
    return outcomeTotalText(this.period, this.viewContext);
  }

  /** The ring read as text, for screen readers: every outcome with its share and count. */
  get outcomeAriaLabel(): string {
    return this.outcomeSegments.map((part) => `${part.label}: ${part.shareText} (${part.valueText})`).join(", ");
  }

  get rejectionFacts(): MiraFact[] {
    return rejectionFacts(this.period, this.viewContext);
  }

  /** The Leistungen card is shown where the service figure (MIRA-PRJ-017) is published for the period. */
  get showServiceStack(): boolean {
    return businessStackAvailable(this.period);
  }

  get stackSegments(): MiraStackedSegmentDef[] {
    return businessStackSegments(this.viewContext, this.period);
  }

  /** A cached formatter, never built in the template (see the base's `activeTrendFormatter`). */
  get stackFormatter(): (value: number) => string {
    return businessStackFormatter(this.viewContext);
  }

  get stackCaption(): string {
    return businessStackCaption(this.viewContext);
  }

  /** The chosen period's count per service, overlaid in the card's legend. */
  get stackLegendValues(): Record<string, string> {
    return businessStackLegendValues(this.period, this.viewContext);
  }

  /**
   * The member clients' branches by id on a group master, "Standort (Mandant)", so its "Projekte je Standort"
   * card names them: the group figures count the members' projects, whose branches the master's own list does
   * not hold. Loaded once on mount, only on a master. Deleted branches keep their name here, since past
   * projects still point at them.
   */
  groupBranchNames: Record<string, string> = {};

  async mounted() {
    if (!this.isGroupMaster) {
      return;
    }
    try {
      const members = await getClientsByIds(getMemberClientIds());
      const names: Record<string, string> = {};
      members.forEach((client) => {
        const company = (client.shortName || client.companyName || client.clientName || "").trim();
        getBranchesOfMemberClient(client).forEach((branch) => {
          if (branch?.id) names[branch.id] = company ? `${branch.name} (${company})` : branch.name;
        });
      });
      this.groupBranchNames = names;
    } catch (error) {
      // Unreadable member clients leave the branches unnamed ("Unbekannter Standort"), never the card broken.
    }
  }

  /**
   * The client's branches by id, deleted ones left out, as every other screen names them; a master's members'
   * branches; on a company's viewing client the company's Mandanten's, named by IRES Cloud (#2610112 X6e),
   * since it cannot read those Mandanten.
   */
  get branchNames(): Record<string, string> {
    const companyView = companyViewOf(this.sections);
    if (companyView) {
      return companyView.branchNames ?? {};
    }
    if (this.isGroupMaster) {
      return this.groupBranchNames;
    }
    return Object.fromEntries(getBranchesOfClient().map((branch) => [branch.id, branch.name]));
  }

  get branchRows(): MiraBar[] {
    return buildBranchRows(this.period, this.branchNames, this.viewContext);
  }

  /**
   * Each published project type id with the name IRES Office shows for it: the client's own label where
   * one is saved, otherwise the default (`formatProjectTypeName`).
   */
  get projectTypeNames(): Record<string, string> {
    const saved = ProjectManagementSettingsModule.projectManagementSettings?.projectTypes;
    const ids = Object.keys(this.period?.metrics["MIRA-PRJ-007"]?.breakdown ?? {});

    return Object.fromEntries(
      ids.filter((id) => !isNaN(Number(id))).map((id) => [id, formatProjectTypeName(this, Number(id), saved)])
    );
  }

  get projectTypeRows(): MiraBar[] {
    return buildProjectTypeRows(this.period, this.projectTypeNames, this.viewContext);
  }

  get damageCauseRows(): MiraBar[] {
    return buildDamageCauseRows(this.period, this.viewContext);
  }

  get damageCauseSubtitle(): string {
    return this.showMarketComparison
      ? damageCauseMarketCoverage(this.period, this.viewContext)
      : damageCauseCoverage(this.period, this.viewContext);
  }

  /**
   * Schadenursachen against the Markt (Taimoor, 2026-10-27): the Markt then the company, the broader scope
   * first as on every comparison card (#2610137), in their colours (Markt je Mandant dropped, #2610112 X6a).
   * Its own two colours, not the timeline's map: with a comparison downwards on too, that map holds the parts
   * (2026-11-02), and both rows fell back to one colour.
   */
  get damageCauseMarket(): MiraDamageCauseMarket {
    const dark = this.$vuetify.theme.dark;
    const ownLabel = this.branchReferenceLabel;
    const colors: Record<string, string> = {
      ...buildStableEntityColors([{ id: MIRA_MARKET_OWN_ID, label: ownLabel, size: null }], dark),
      [MIRA_MARKET_PROJECTS_ID]: dark ? COLOR_MIRA_MARKET_BAR_DARK : COLOR_MIRA_MARKET_BAR,
    };
    const periodKey = this.selectedPeriod;
    return buildDamageCauseMarket(
      [
        { id: MIRA_MARKET_PROJECTS_ID, label: this.marketLabel, period: this.marketProjectsDocument?.periods[periodKey] ?? null, isMarket: true },
        { id: MIRA_MARKET_OWN_ID, label: ownLabel, period: this.period, isMarket: false },
      ].map((entity) => ({ ...entity, color: colors[entity.id] })),
      this.viewContext
    );
  }

  /**
   * Only this section's figures waiting on data: the other sections have their own screens. Computed
   * here rather than through `super`, which vue-class-component's computed getters do not support.
   */
  get unlocks(): MiraSectionUnlocks[] {
    return collectUnlocks(this.sections, this.selectedPeriod).filter(
      (group) => group.section === MiraSection.BUSINESS_DEVELOPMENT
    );
  }

  trendFormatter(metricId: string): (value: number) => string {
    return businessTrendFormatter(metricId, this.viewContext, this.period);
  }
}
